import moment from 'moment';

export const DEFAULT_LIST_VIEW_CONFIG = {
  default_date_range: 'current_month',
  record_statuses: ['true', 'false'],
};

function appendRecordStatusParams(params, config) {
  if (config?.record_statuses?.length) {
    params.record_statuses = config.record_statuses.join(',');
  }
  return params;
}

export function resolveDateRangeFromConfig(config, viewKey = null) {
  const preset = config?.default_date_range || 'current_month';
  const today = moment();

  switch (preset) {
    case 'today':
      return [today.clone().startOf('day'), today.clone().endOf('day')];
    case 'current_month':
      return [today.clone().startOf('month'), today.clone()];
    case 'last_two_months':
      return [today.clone().subtract(2, 'months').startOf('month'), today.clone()];
    case 'last_three_months':
      return [today.clone().subtract(3, 'months').startOf('month'), today.clone()];
    case 'custom':
      if (config?.custom_from_date && config?.custom_upto_date) {
        return [
          moment(config.custom_from_date, 'DD-MM-YYYY'),
          moment(config.custom_upto_date, 'DD-MM-YYYY'),
        ];
      }
      return [today.clone().startOf('month'), today.clone()];
    default:
      return [today.clone().startOf('month'), today.clone()];
  }
}

export function formatResolvedRange(config, viewKey) {
  const [fromDate, toDate] = resolveDateRangeFromConfig(config, viewKey);
  const preset = config?.default_date_range || 'current_month';

  if (viewKey === 'work_schedule' && preset !== 'custom' && preset !== 'today') {
    return `${fromDate.format('DD-MM-YYYY')} → ${moment().add(90, 'days').format('DD-MM-YYYY')} (includes upcoming)`;
  }

  if (preset === 'today') {
    return fromDate.format('DD-MM-YYYY');
  }

  return `${fromDate.format('DD-MM-YYYY')} → ${toDate.format('DD-MM-YYYY')}`;
}

export function buildWorkScheduleQueryParams(config) {
  const preset = config?.default_date_range || 'current_month';
  const [fromDate, toDate] = resolveDateRangeFromConfig(config, 'work_schedule');
  const params = {
    from_date: fromDate.format('DD-MM-YYYY'),
  };

  if (preset === 'today' || preset === 'custom') {
    params.upto_date = toDate.format('DD-MM-YYYY');
  } else {
    // Cap upcoming jobs (backend also enforces ~90 days) so lists stay fast.
    params.include_upcoming = '1';
    params.upto_date = moment.max(toDate, moment().add(90, 'days')).format('DD-MM-YYYY');
  }

  if (config?.schedule_statuses?.length) {
    params.schedule_statuses = config.schedule_statuses.join(',');
  }
  if (config?.record_statuses?.length) {
    params.record_statuses = config.record_statuses.join(',');
  }
  if (config?.plant_ids?.length) {
    params.plant_ids = config.plant_ids.join(',');
  }
  return params;
}

export function buildDeliveryChallanQueryParams(config) {
  const [fromDate, toDate] = resolveDateRangeFromConfig(config, 'delivery_challan');
  const params = {
    from_date: fromDate.format('DD-MM-YYYY'),
    upto_date: toDate.format('DD-MM-YYYY'),
  };
  if (config?.plant_ids?.length) {
    params.plant_ids = config.plant_ids.join(',');
  }
  return appendRecordStatusParams(params, config);
}

export function buildDesignMixQueryParams(config) {
  const [fromDate, toDate] = resolveDateRangeFromConfig(config, 'design_mix');
  const params = {
    from_date: fromDate.format('DD-MM-YYYY'),
    upto_date: toDate.format('DD-MM-YYYY'),
  };
  if (config?.plant_ids?.length) {
    params.plant_ids = config.plant_ids.join(',');
  }
  return appendRecordStatusParams(params, config);
}

export function buildSalesOrderQueryParams(config) {
  const [fromDate, toDate] = resolveDateRangeFromConfig(config, 'sales_order');
  const params = {
    from_date: fromDate.format('DD-MM-YYYY'),
    upto_date: toDate.format('DD-MM-YYYY'),
  };
  // Do not send plant_ids for sales orders. Orders are company-scoped; a bad
  // plant→company mapping was returning an empty list. Date + record status only.
  return appendRecordStatusParams(params, config);
}

function buildStandardListQueryParams(config, viewKey, includePlants = false) {
  const [fromDate, toDate] = resolveDateRangeFromConfig(config, viewKey);
  const params = {
    from_date: fromDate.format('DD-MM-YYYY'),
    upto_date: toDate.format('DD-MM-YYYY'),
  };
  if (includePlants && config?.plant_ids?.length) {
    params.plant_ids = config.plant_ids.join(',');
  }
  return appendRecordStatusParams(params, config);
}

export function buildGoodsReceiptNoteQueryParams(config) {
  return buildStandardListQueryParams(config, 'goods_receipt_note', true);
}

export function buildConsumptionNoteQueryParams(config) {
  return buildStandardListQueryParams(config, 'consumption_note', true);
}

export function buildDieselConsumptionQueryParams(config) {
  return buildStandardListQueryParams(config, 'diesel_consumption', true);
}

export function buildQuotationQueryParams(config) {
  return buildStandardListQueryParams(config, 'quotation', false);
}

export function buildPurchaseOrderQueryParams(config) {
  return buildStandardListQueryParams(config, 'purchase_order', false);
}

export function buildCheckInQueryParams(config) {
  return buildStandardListQueryParams(config, 'check_in', true);
}

export function buildSalesInvoiceQueryParams(config) {
  const params = buildStandardListQueryParams(config, 'sales_invoice', false);
  params.plant_ids = (config?.plant_ids || []).join(',');
  params.customer_ids = config?.customer_ids?.length
    ? config.customer_ids.join(',')
    : '*';
  return params;
}

export function buildCubeTestReportQueryParams(config) {
  return buildStandardListQueryParams(config, 'cube_test_report', true);
}

export function appendQueryParams(url, params) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const queryString = query.toString();
  return queryString ? `${url}?${queryString}` : url;
}
